import ChangeHistoryIcon from '@mui/icons-material/ChangeHistory';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SyncIcon from '@mui/icons-material/Sync';
import { Button, Checkbox, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import classnames from "classnames";
import React, { Fragment, useCallback, useContext, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { DashboardsTableContext, DashboardsTableFilters, HeaderIcons } from '../../../contexts/TablesContext/DashboardsTableProvider';
import { WorkspaceContext } from '../../../contexts/WorkspaceContext';
import theme from "../../../theme";
import CompaniesMultiAction from "../multi-action/CompaniesMultiAction";

const useStyles = makeStyles((theme) => ({
    row: {
        minWidth: '100%',
        width: 'fit-content',
        height: 'fit-content',
    },
    header: {
        fontFamily: 'Inter',
        fontSize: '1.1rem',
        fontWeight: '600',
        textTransform: 'none',
        color: theme.colors.neutral['600'],
        paddingLeft: 'unset !important',
        "&:hover": {
            background: 'unset',
        }
    },
    hoverHeader: {
        color: theme.colors.neutral['600'],
        cursor: 'pointer',
        "&:hover": {
            color: theme.colors.neutral['900'],
            "& > svg": {
                color: theme.colors.neutral['900'],
                stroke: `${theme.colors.neutral['900']} !important`,
            },
        },
    },
    checkbox: {
        minWidth: 24,
        maxWidth: 24,
    },
    cell: {
        minWidth: 180,
        maxWidth: 180,
        minHeight: 60,
        maxHeight: 60,
        padding: '0 10px',
    },
    narrowCell: {
        minWidth: 150,
        maxWidth: 150,
    },
    narrowerCell: {
        minWidth: 50,
        maxWidth: 50,
    },
    customNarrowCell: {
        minWidth: 20,
        maxWidth: 20,
    },
    wideCell: {
        minWidth: 280,
        maxWidth: 280,
    },
    widerCell: {
        minWidth: 380,
        maxWidth: 380,
    },
    fullWidthCell: {
        minWidth: 0,
        maxWidth: 'unset !important',
        width: '100%',
    },
    hiddenCell: {
        display: 'none',
    },
    endCell: {
        minWidth: 4,
        maxWidth: 4,
        padding: 'unset',
    },
    checkBox: {
        padding: 'unset !important',
        marginRight: '4px !important',
        "& > svg": {
            width: '20px !important',
            height: '20px !important',
        }
    },
    addSector: {
        minWidth: 'unset',
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        color: theme.palette.primary.light,
        textTransform: 'none',
        "&:hover": {
            background: 'unset',
        }
    },
    customCell: {
        minWidth: 240,
        maxWidth: 240,
    },
    sortHeaders: {
        minWidth: 'unset',
        fontFamily: 'Inter',
        fontSize: '1.1rem',
        fontWeight: '600',
        color: theme.colors.neutral['600'],
        textTransform: 'none',
        "&:hover": {
            background: 'unset',
        }
    },
    clear: {
        minWidth: 'unset',
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 600,
        color: theme.palette.warning.dark,
        textTransform: 'none',
        "&:hover": {
            background: 'unset',
        }
    },
}));

export const TableHeaders = ['Name', 'Status', 'Website', 'Fit score', 'Stage', 'Sectors', 'Description', 'Location', 'Data from...', 'Owner', 'Source', 'Fundraising', 'Revenue', 'Valuation', 'Margins', 'Request Intro', '[2]', 'Added', '[3]'];

const DashboardsTableHeader: React.FC<{
    columns: string[],
    filters: DashboardsTableFilters,
    actions?: string[],
    multiAction?: boolean,
    noStatus?: boolean,
    xAdjust?: number,
    reducedWidth?: boolean,
    onFilter?: ({ statuses, stages, owners, sources, added } : DashboardsTableFilters) => void,
    onRefreshScores?: () => void,
}> = ({ columns, filters, actions, multiAction,  noStatus, xAdjust, reducedWidth,
    onFilter, onRefreshScores }) => {
    const classes = useStyles();
    const { workspace } = useContext(WorkspaceContext);
    const { sortBy, setSortBy } = useContext(DashboardsTableContext);
    const anchorRefs = useRef<HTMLDivElement[]>([]);
    const multiActionRef = useRef<any>(null);

    const props = { actions, xAdjust };

    const showColumn = useCallback((column: string) => columns?.includes(column), [columns]);

    const handleColumnSort = useCallback((column: string) => {
            setSortBy(column);
        if (['Fit score'].includes(column)) {
            setSortBy(`${column} +[${uuidv4()}]`);
        } else {
            setSortBy(column);
        }
    }, [setSortBy]);

    useEffect(() => {
        if (sortBy !== undefined)
            setSortBy(sortBy);
    // eslint-disable-next-line
    }, [sortBy]);

    return (<>
        <Stack direction="row" className={classes.row} alignItems="flex-start" justifyContent="flex-start" ref={multiActionRef}>
            <Stack alignItems="center" justifyContent="center" height="60px">
                <Checkbox edge="end"
                    className={classes.checkBox}
                    checked={filters.added?.some(added => added === '+Infinity')}
                    onClick={() => onFilter?.({ added: !filters.added?.some(added => added === '+Infinity') ? ['+Infinity'] : [] })}
                    disableRipple />
            </Stack>
            {TableHeaders.map((header, i, self) => (i === 1 ? !noStatus : showColumn(header)) && (
                <Fragment key={'dashboads-table-header-74-' + i}>
                    <Stack direction="column" className={classnames(classes.cell,
                        (i === 0) && !reducedWidth && classes.customCell,
                        (i === 3) && !reducedWidth && classes.customCell,
                        (i === 5) && classes.wideCell,
                        (i === 6) && classes.widerCell,
                        (i === 7) && classes.customCell,
                        (i === 9) && classes.customCell,
                        (i === 10) && classes.wideCell,
                        (i === self.length - 3) && classes.fullWidthCell,
                        (i === self.length - 2) && classes.narrowCell,
                        (i === self.length - 1) && classes.narrowerCell)}
                        alignItems="flex-start" justifyContent="center" >
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start"
                            ref={el => anchorRefs.current[i] = el as HTMLDivElement}>
                            {(['Fit score', 'Added'].includes(header)) ? (
                                <Button variant="text"
                                    className={classes.header}
                                    onClick={() => handleColumnSort(header)}
                                    disableRipple>
                                    {header}
                                    {sortBy.includes(header) ? (
                                        <ArrowDropDownIcon sx={{ fill: theme.palette.primary.main, transform: 'scale(1.5, 2.5)', translate: '4px 0px'}} />
                                    ) : (
                                        <ChangeHistoryIcon sx={{ fill: theme.colors.neutral['600'],
                                            transform: 'rotate(180deg) scale(0.85, 0.85)', translate: '4px 1px' }} />
                                    )}
                                </Button>
                            ) :  (!header.startsWith('[') && !header.endsWith(']')) && (<>{
                                    Object.keys(HeaderIcons).includes(header) ? (
                                        <Stack direction="row" className={classnames(classes.header, classes.hoverHeader)}
                                            spacing={1} alignItems="center" justifyContent="flex-start">
                                            {!!HeaderIcons[header] && HeaderIcons[header]}
                                            <span>{header}</span>
                                        </Stack>
                                ) : (
                                <Typography className={classes.header}
                                    sx={{ color: theme.colors.neutral['600'],
                                        cursor: 'auto',
                                    }}> {header} </Typography>
                            )}</>)}
                            {(header === 'Name') && (
                                <ExpandMoreIcon
                                    sx={{ color: theme.colors.neutral['600'],
                                        cursor: 'pointer',
                                        "&:hover": { color: theme.colors.neutral['900'] },
                                    }} />
                            )}
                            {(header === 'Status') && (!!filters?.statuses?.length ? (
                                <Stack direction="row" alignItems="center" justifyContent="flex-start">
                                    <FilterAltIcon sx={{ width: 24, height: 24, color: theme.palette.primary.main }} />
                                    <Button variant="text"
                                        className={classes.clear}
                                        onClick={() => onFilter?.({ statuses: [] })}
                                        disableRipple> {'clear'} </Button>
                                </Stack>
                            ): ( <ExpandMoreIcon
                                    sx={{ color: theme.colors.neutral['600'],
                                        cursor: 'pointer',
                                        "&:hover": { color: theme.colors.neutral['900'] },
                                    }} />
                            ))}
                            {(header === 'Stage') && (!!filters?.stages?.length ? (
                                <Stack direction="row" alignItems="center" justifyContent="flex-start">
                                    <FilterAltIcon sx={{ width: 24, height: 24, color: theme.palette.primary.main }} />
                                    <Button variant="text"
                                        className={classes.clear}
                                        onClick={() => onFilter?.({ stages: [] })}
                                        disableRipple> {'clear'} </Button>
                                </Stack>
                            ) : ( <ExpandMoreIcon
                                    sx={{ color: theme.colors.neutral['600'],
                                        cursor: 'pointer',
                                        "&:hover": { color: theme.colors.neutral['900'] },
                                    }} />
                            ))}
                            {(header === 'Owner') && (!!filters?.owners?.length ? (
                                <Stack direction="row" alignItems="center" justifyContent="flex-start">
                                    <FilterAltIcon sx={{ width: 24, height: 24, color: theme.palette.primary.main }} />
                                    <Button variant="text"
                                        className={classes.clear}
                                        onClick={() => onFilter?.({ owners: [] })}
                                        disableRipple> {'clear'} </Button>
                                </Stack>
                            ) : ( <ExpandMoreIcon
                                    sx={{ color: theme.colors.neutral['600'],
                                        cursor: 'pointer',
                                        "&:hover": { color: theme.colors.neutral['900'] },
                                    }} />
                            ))}
                            {(header === 'Source') && (!!filters?.sources?.length ? (
                                <Stack direction="row" alignItems="center" justifyContent="flex-start">
                                    <FilterAltIcon sx={{ width: 24, height: 24, color: theme.palette.primary.main }} />
                                    <Button variant="text"
                                        className={classes.clear}
                                        onClick={() => onFilter?.({ sources: [] })}
                                        disableRipple> {'clear'} </Button>
                                </Stack>
                            ) : ( <ExpandMoreIcon
                                    sx={{ color: theme.colors.neutral['600'],
                                        cursor: 'pointer',
                                        "&:hover": { color: theme.colors.neutral['900'] },
                                    }} />
                            ))}
                            {!!onRefreshScores && (header === 'Fit score') && (<>
                                <Tooltip placement="top-start" title={
                                    <Typography component="span" fontSize="0.95rem" fontWeight={500}>
                                        {workspace?.isShared ? `Fit scores are calculated based on your deal preferences.`
                                            : `Calculate fit scores for each company based on ${workspace?.name || 'the workspace'}'s preferences.`}
                                    </Typography>}
                                    componentsProps={{tooltip:{sx:{
                                        display: 'flex', alignItems: 'center', borderRadius: '12px', transform: 'translate(-90px) !important',
                                        backgroundColor: 'rgba(150, 150, 150, 0.95)', width: '210px', height: '110px',  padding: 2,
                                    }}}}>
                                    <IconButton onClick={() => onRefreshScores?.()} sx={{ padding: 'unset' }} disableRipple>
                                        <SyncIcon sx={{ fill: theme.palette.primary.main, transform: 'scale(1.05, 1.05)', translate: '-5px 1px' }} />
                                    </IconButton>
                                </Tooltip>
                            </>)}
                        </Stack>
                    </Stack>
                    {(header === 'Sectors') && showColumn(header) && (
                        <Stack className={classnames(classes.cell, classes.customNarrowCell)} />
                    )}
                </Fragment>
            ))}
            <Stack className={classnames(classes.cell, classes.endCell)} />
        </Stack>
        {multiAction && (<CompaniesMultiAction {...props} ref={multiActionRef} />)}
    </>);
}

export default DashboardsTableHeader;